import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Check, X, RotateCcw, Wallet, Receipt } from "lucide-react";
import { toast } from "sonner";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import {
  formatRupiah, formatDate, policyLineText,
  REFUND_STATUS_LABELS, REFUND_STATUS_STYLES,
} from "@/components/booking/bookingMeta";

const FILTERS = [
  { value: "all", label: "Semua" },
  { value: "pending", label: "Menunggu" },
  { value: "approved", label: "Disetujui" },
  { value: "processed", label: "Berhasil" },
  { value: "rejected", label: "Ditolak" },
];

// Shared refund management view for Owner (own properties) and Admin (all).
export default function RefundManager({ role = "owner", testidPrefix = "owner-refunds" }) {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [acting, setActing] = useState(null);

  const base = role === "admin" ? "/admin/refunds" : "/owner/refunds";

  const load = (status = filter) => {
    setLoading(true); setError(false);
    const qs = status && status !== "all" ? `?status=${status}` : "";
    apiClient.get(`${base}${qs}`)
      .then((r) => setRefunds(r.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const act = async (id, action) => {
    setActing(id + action);
    try {
      if (action === "reject") {
        const reason = window.prompt("Alasan penolakan refund (opsional):") ?? "";
        await apiClient.post(`/refunds/${id}/reject`, { reason: reason.trim() || null });
        toast.success("Refund ditolak.");
      } else if (action === "approve") {
        await apiClient.post(`/refunds/${id}/approve`);
        toast.success("Refund disetujui. Menunggu pemrosesan Duitku.");
      } else {
        const { data } = await apiClient.post(`/refunds/${id}/sync`);
        toast.success(`Status refund: ${REFUND_STATUS_LABELS[data.status] || data.status}`);
      }
      load(filter);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  return (
    <div data-testid={`${testidPrefix}-page`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Permintaan Refund</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Kelola pembatalan booking & refund. Status menjadi <b>Berhasil</b> hanya setelah Duitku mengonfirmasi.
          </p>
        </div>
        <button onClick={() => load(filter)} data-testid={`${testidPrefix}-refresh`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
          <RefreshCw size={14} /> Muat Ulang
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid={`${testidPrefix}-filters`}>
        {FILTERS.map((f) => (
          <button key={f.value} onClick={() => setFilter(f.value)}
            data-testid={`${testidPrefix}-filter-${f.value}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              filter === f.value ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 grid place-items-center py-16"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : error ? (
        <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/10 p-8 text-center" data-testid={`${testidPrefix}-error`}>
          <p className="text-sm text-destructive">Gagal memuat permintaan refund.</p>
          <button onClick={() => load(filter)} className="mt-3 text-sm font-semibold text-primary hover:underline">Coba lagi</button>
        </div>
      ) : refunds.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-secondary/30 p-12 text-center" data-testid={`${testidPrefix}-empty`}>
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-secondary text-primary mx-auto mb-4"><Wallet size={26} /></span>
          <h3 className="font-heading text-lg font-medium text-foreground">Belum ada permintaan refund.</h3>
        </div>
      ) : (
        <div className="mt-6 space-y-4" data-testid={`${testidPrefix}-list`}>
          {refunds.map((r, i) => (
            <div key={r.id} data-testid={`${testidPrefix}-item-${i}`}
              className="rounded-2xl border border-border bg-card p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-lg font-semibold truncate">{r.property_name || "Hunian"}</h3>
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${REFUND_STATUS_STYLES[r.status] || ""}`}
                      data-testid={`${testidPrefix}-status-${i}`} data-refund-status={r.status}>
                      {REFUND_STATUS_LABELS[r.status] || r.status}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Kamar {r.room_number || "-"} · Customer: <span className="text-foreground font-medium">{r.customer_name || "-"}</span>
                  </p>
                  {r.reason && <p className="mt-1 text-sm text-muted-foreground">Alasan: {r.reason}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-muted-foreground">Total Refund</p>
                  <p className="font-heading text-xl font-bold text-primary" data-testid={`${testidPrefix}-total-${i}`}>
                    {formatRupiah(r.total_refund_amount)}
                  </p>
                </div>
              </div>

              <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
                <div className="rounded-xl border border-border bg-secondary/20 p-3 space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Pembayaran (SUCCESS)</p>
                  <Row label="Room Dibayar" value={formatRupiah(r.room_paid)} />
                  <Row label="Deposit Dibayar" value={formatRupiah(r.deposit_paid)} />
                  <Row label="Total Dibayar" value={formatRupiah(r.total_paid)} strong />
                </div>
                <div className="rounded-xl border border-border bg-secondary/20 p-3 space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Estimasi Refund</p>
                  <Row label="Refund Room" value={formatRupiah(r.room_refund_amount)} testid={`${testidPrefix}-room-refund-${i}`} />
                  <Row label="Refund Deposit" value={formatRupiah(r.deposit_refund_amount)} testid={`${testidPrefix}-deposit-refund-${i}`} />
                  <Row label="Kebijakan Room" value={policyLineText(r.cancellation_policy?.room_refund_type, r.cancellation_policy?.room_refund_rate)} small />
                  <Row label="Kebijakan Deposit" value={policyLineText(r.cancellation_policy?.deposit_refund_type, r.cancellation_policy?.deposit_refund_rate)} small />
                </div>
              </div>

              {(r.source_payments?.length > 0) && (
                <div className="mt-3 rounded-xl border border-border bg-secondary/10 p-3" data-testid={`${testidPrefix}-sources-${i}`}>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                    <Receipt size={13} /> Transaksi Pembayaran Asal (Duitku)
                  </p>
                  <div className="mt-1.5 space-y-1">
                    {r.source_payments.map((sp, j) => (
                      <div key={j} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-muted-foreground truncate">
                          {sp.payment_purpose} · Ref: <span className="font-mono text-foreground">{sp.duitku_reference || "-"}</span>
                        </span>
                        <span className="font-medium text-foreground shrink-0">{formatRupiah(sp.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {r.disburse_note && (
                <p className="mt-2 text-xs text-muted-foreground" data-testid={`${testidPrefix}-note-${i}`}>{r.disburse_note}</p>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {r.status === "pending" && (
                  <>
                    <button onClick={() => act(r.id, "approve")} disabled={acting === r.id + "approve"}
                      data-testid={`${testidPrefix}-approve-${i}`}
                      className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold text-emerald-700 border border-emerald-200 hover:bg-emerald-50 transition-colors disabled:opacity-60">
                      {acting === r.id + "approve" ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Approve Refund
                    </button>
                    <button onClick={() => act(r.id, "reject")} disabled={acting === r.id + "reject"}
                      data-testid={`${testidPrefix}-reject-${i}`}
                      className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold text-rose-600 border border-rose-200 hover:bg-rose-50 transition-colors disabled:opacity-60">
                      {acting === r.id + "reject" ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />} Reject
                    </button>
                  </>
                )}
                {(r.status === "approved" || r.status === "failed") && (
                  <button onClick={() => act(r.id, "sync")} disabled={acting === r.id + "sync"}
                    data-testid={`${testidPrefix}-sync-${i}`}
                    className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold text-sky-700 border border-sky-200 hover:bg-sky-50 transition-colors disabled:opacity-60">
                    {acting === r.id + "sync" ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Cek Status Duitku
                  </button>
                )}
                <span className="ml-auto text-xs text-muted-foreground self-center">
                  Diminta {formatDate(r.requested_at)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, strong, small, testid }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={`text-muted-foreground ${small ? "text-xs" : ""}`}>{label}</span>
      <span className={`${strong ? "font-bold" : "font-medium"} text-foreground ${small ? "text-xs" : ""}`} data-testid={testid}>{value}</span>
    </div>
  );
}
